(() => {
  'use strict';
  // The dialog ID also creates a named window property in browsers.
  if (typeof window.NiralaQuickOrder?.open === 'function') return;
  const dialog = document.querySelector('[data-quick-order-dialog]');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  const body = dialog.querySelector('[data-quick-order-body]');
  const loading = dialog.querySelector('[data-quick-order-loading]');
  const failure = dialog.querySelector('[data-quick-order-failure]');
  const fallback = dialog.querySelector('[data-quick-order-fallback]');
  const retryButton = dialog.querySelector('[data-quick-order-retry]');
  const fragmentCache = new Map();
  const loadError = dialog.querySelector('[data-quick-order-load-error]');
  const title = dialog.querySelector('[data-quick-order-title]');
  const closeButton = dialog.querySelector('[data-quick-order-close]');
  const defaultTitle = title.textContent;
  let request = null;
  let sequence = 0;
  let trigger = null;
  let state = null;
  let busy = false;
  let historyEntry = false;
  let returning = false;
  const originalTitle = document.title;
  function restoreURL() {
    document.title = originalTitle;
    if (historyEntry && history.state?.niralaQuickOrder) {
      historyEntry = false;
      returning = true;
      history.back();
    }
  }
  window.addEventListener('popstate', () => {
    if (returning) { returning = false; return; }
    historyEntry = false;
    if (history.state?.niralaQuickOrder) {
      open({ href: location.href, isConnected: false }, true);
    } else {
      closeLayer(); clear(); document.title = originalTitle;
    }
  });
  let ignoredCloseEvents = 0;
  document.addEventListener('keydown', event => { if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') document.documentElement.dataset.npKeyboard = 'true'; });
  document.addEventListener('pointerdown', () => { document.documentElement.dataset.npKeyboard = 'false'; });

  function closeLayer() {
    if (!dialog.open) return;
    ignoredCloseEvents += 1;
    dialog.close();
  }
  function clear(restoreFocus = true) {
    restoreURL();
    request?.abort();
    request = null;
    sequence += 1;
    state = null;
    body.replaceChildren();
    loading.hidden = true;
    failure.hidden = true;
    dialog.removeAttribute('aria-busy');
    if (restoreFocus && trigger?.isConnected) {
      if (document.documentElement.dataset.npKeyboard === 'true') trigger.focus({ preventScroll: true });
      else trigger.blur();
    }
    trigger = null;
  }
  function dismiss() {
    if (busy) return;
    closeLayer();
    clear();
  }
  function money(amount, currency, locale) {
    if (typeof window.Nirala?.formatMoney === 'function') return window.Nirala.formatMoney(amount, currency);
    return new Intl.NumberFormat(locale || document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'PKR' }).format(amount / 100);
  }
  function selectedVariant() {
    if (!state) return null;
    if (!state.groups.length) return state.variants.find(variant => String(variant.id) === state.initialVariant) || state.variants[0];
    const options = state.groups.map(group => group.querySelector('[data-quick-option-value]:checked')?.value);
    if (options.some(value => value === undefined)) return null;
    return state.variants.find(variant => variant.options.length === options.length && variant.options.every((value, index) => value === options[index])) || null;
  }
  function syncConditionalOptions() {
    if (!state) return;
    const parent = state.groups.findIndex(group => /crust|base/i.test(group.dataset.optionName || ''));
    const parentValue = parent < 0 ? null : state.groups[parent].querySelector(':checked')?.value;
    state.groups.forEach((group, index) => {
      const flavor = /flavou?r/i.test(group.dataset.optionName || '');
      const conditional = dialog.dataset.conditionalOptions !== 'false' && parent >= 0 && flavor && index !== parent;
      const details = group.closest('[data-quick-option-group]');
      details.hidden = conditional && !parentValue;
      const inputs = [...group.querySelectorAll('[data-quick-option-value]')];
      inputs.forEach(input => {
        const compatible = !conditional || !parentValue || state.variants.some(v => v.options[parent] === parentValue && v.options[index] === input.value);
        input.closest('label').hidden = !compatible;
        input.disabled = busy || details.hidden || !compatible;
        if (!compatible || details.hidden) input.checked = false;
      });
      // A fixed size is already known. Never guess a multi-value selection.
      if (inputs.length === 1 && !details.hidden) inputs[0].checked = true;
    });
  }
  function requiredOptionsComplete() {
    return Boolean(state && state.groups.every(group => group.querySelector('[data-quick-option-value]:checked')));
  }
  function emit(name, detail = {}) {
    if (!state) return;
    state.form.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { form: state.form, variant: state.variant, quantity: Number(state.quantity.value), ...detail } }));
  }
  function showError(message) {
    if (!state) return;
    state.error.textContent = String(message || dialog.dataset.failedMessage);
    state.error.hidden = false;
  }
  function update(preserveError = false) {
    if (!state) return;
    syncConditionalOptions();
    const variant = selectedVariant();
    state.variant = variant;
    const valid = Boolean(variant?.available);
    state.id.value = variant ? String(variant.id) : '';
    state.id.disabled = !valid;
    const min = Math.max(1, Number(variant?.min) || 1);
    const step = Math.max(1, Number(variant?.step) || 1);
    const max = variant?.max === null || variant?.max === undefined ? null : Number(variant.max);
    state.quantity.min = String(min);
    state.quantity.step = String(step);
    if (max !== null && Number.isFinite(max)) state.quantity.max = String(Math.max(0, max));
    else state.quantity.removeAttribute('max');
    const quantity = Number(state.quantity.value);
    const quantityValid = Number.isSafeInteger(quantity) && quantity >= min && (quantity - min) % step === 0 && (max === null || quantity <= max);
    const addonsValid = typeof window.NiralaProductAddons?.isValid !== 'function' || window.NiralaProductAddons.isValid(state.form, quantity);
    if (variant && !variant.available) state.add.dataset.soldOut = 'true';
    else delete state.add.dataset.soldOut;
    window.NiralaBusyControls.set(state.add, busy, !valid || !quantityValid || !addonsValid);
    state.label.textContent = variant && !variant.available ? dialog.dataset.soldLabel : state.add.dataset.addLabel;
    state.unavailable.hidden = valid;
    if (!valid) state.unavailable.textContent = !requiredOptionsComplete() ? dialog.dataset.requiredMessage : variant ? dialog.dataset.soldLabel : dialog.dataset.unavailableMessage;
    state.groups.forEach(group => {
      const selected = group.querySelector('[data-quick-option-value]:checked');
      const details = group.closest('[data-quick-option-group]');
      const summary = details?.querySelector('[data-quick-selected-value]');
      if (summary) summary.textContent = selected?.value || '';
      if (details) details.dataset.confirmed = String(Boolean(selected));
      const status = details?.querySelector('[data-quick-option-status]');
      if (status) status.textContent = selected ? dialog.dataset.selectedLabel : dialog.dataset.requiredLabel;
    });
    const isSale = Boolean(variant && Number.isSafeInteger(variant.compareAtPrice) && variant.compareAtPrice > variant.price);
    if (state.compare) {
      state.compare.hidden = !isSale || !state.showCompare;
      state.compare.textContent = isSale ? variant.compareAtPriceText : '';
    }
    if (state.saleBadge) {
      state.saleBadge.hidden = !isSale || !state.showSaleBadge;
      state.saleBadge.textContent = isSale ? variant.saleBadgeText : '';
    }
    if (variant) {
      const addonsTotal = Number(window.NiralaProductAddons?.getUnitTotal?.(state.form)) || 0;
      state.price.textContent = money(variant.price + addonsTotal, state.currency, state.locale);
      state.total.textContent = quantityValid ? money((variant.price + addonsTotal) * quantity, state.currency, state.locale) : '—';
    } else {
      const selections = state.groups.map(group => group.querySelector('[data-quick-option-value]:checked')?.value);
      const compatible = state.variants.filter(item => item.available && selections.every((value, index) => value === undefined || item.options[index] === value));
      const starting = compatible.reduce((lowest, item) => !lowest || item.price < lowest.price ? item : lowest, null);
      state.price.textContent = starting ? `${dialog.dataset.fromLabel || 'From'} ${starting.priceText}` : dialog.dataset.unavailableMessage;
      state.total.textContent = '—';
    }
    const imageUrl = variant?.image || state.defaultImage;
    if (imageUrl && state.image && state.image.getAttribute('src') !== imageUrl) {
      state.image.src = imageUrl;
      state.image.removeAttribute('srcset');
    }
    window.NiralaBusyControls.set(state.form.querySelector('[data-quick-quantity-change="-1"]'), busy, !Number.isFinite(quantity) || quantity <= min, false);
    window.NiralaBusyControls.set(state.form.querySelector('[data-quick-quantity-change="1"]'), busy, !valid || (max !== null && quantity + step > max), false);
    if (!preserveError) state.error.hidden = true;
    emit('nirala:quick-order-updated');
    const completionKey = valid ? String(variant.id) : '';
    if (completionKey && completionKey !== state.completionKey) {
      state.completionKey = completionKey;
      emit('nirala:quick-order-options-complete');
    } else if (!completionKey) state.completionKey = '';
  }
  function setBusy(value) {
    busy = value;
    window.NiralaBusyControls.set(closeButton, value, false, false);
    if (!state) return;
    state.form.setAttribute('aria-busy', String(value));
    state.form.querySelectorAll('[data-quick-option-value]').forEach(input => { input.disabled = value; });
    state.form.querySelectorAll('textarea,input[type="number"]').forEach(input => { input.readOnly = value; });
    window.NiralaProductAddons?.setBusy?.(state.form, value);
    update(true);
  }
  function validateVariants(raw, count) {
    if (!Array.isArray(raw) || !raw.length || raw.length > 250 || (count && raw.length !== count)) throw new Error('Incomplete product options');
    raw.forEach(variant => {
      if (!Number.isSafeInteger(variant.id) || !Number.isSafeInteger(variant.price) || variant.price < 0 || typeof variant.priceText !== 'string' || typeof variant.available !== 'boolean' || !Array.isArray(variant.options) || variant.options.some(value => typeof value !== 'string')) throw new Error('Invalid product options');
      if (variant.image) {
        const image = new URL(variant.image, window.location.href);
        if (!['https:', 'http:'].includes(image.protocol)) throw new Error('Invalid image');
      }
    });
    return raw;
  }
  function mount(fragment, variants) {
    // This is our same-origin Shopify section; remove executable markup defensively.
    // Retain the validated, non-executable variant JSON for the discount
    // renderer. Removing it left the popup at Shopify's undiscounted price.
    fragment.querySelectorAll('script:not([type="application/json"][data-quick-order-variants]),iframe,object,embed,style,link,meta,base').forEach(node => node.remove());
    fragment.querySelectorAll('*').forEach(node => {
      [...node.attributes].forEach(attribute => {
        if (/^on/i.test(attribute.name) || (['href', 'src', 'action', 'formaction'].includes(attribute.name) && /^\s*(javascript|data):/i.test(attribute.value))) node.removeAttribute(attribute.name);
      });
    });
    body.replaceChildren(fragment);
    const form = fragment.querySelector('[data-quick-order-form]');
    if (!form) throw new Error('Missing product form');
    const action = new URL(form.action, window.location.href);
    if (action.origin !== window.location.origin) throw new Error('Invalid product form');
    const groups = [...form.querySelectorAll('[data-quick-option-index]')].sort((a, b) => Number(a.dataset.quickOptionIndex) - Number(b.dataset.quickOptionIndex));
    state = {
      form, variants, groups,
      initialVariant: fragment.dataset.selectedVariant,
      currency: fragment.dataset.currency,
      locale: fragment.dataset.locale,
      quantity: form.querySelector('[data-quick-quantity]'),
      id: form.elements.namedItem('id'),
      add: form.querySelector('[data-quick-add]'),
      label: form.querySelector('[data-quick-add-label]'),
      error: form.querySelector('[data-quick-error]'),
      unavailable: form.querySelector('[data-quick-unavailable]'),
      total: form.querySelector('[data-quick-total]'),
      price: form.querySelector('[data-quick-price]'),
      compare: form.querySelector('[data-quick-compare]'),
      saleBadge: form.querySelector('[data-quick-sale-badge]'),
      showCompare: fragment.dataset.showCompare !== 'false',
      showSaleBadge: fragment.dataset.showSaleBadge !== 'false',
      image: form.querySelector('[data-quick-image]')
    };
    state.defaultImage = fragment.dataset.defaultImage || state.image?.getAttribute('src') || '';
    if (state.defaultImage && !['https:', 'http:'].includes(new URL(state.defaultImage, window.location.href).protocol)) throw new Error('Invalid default image');
    if (!state.quantity || !state.id || !state.add || !state.error || !state.total || !state.price || !state.label || !state.unavailable) throw new Error('Incomplete product form');
    if (groups.length && variants.some(variant => variant.options.length !== groups.length)) throw new Error('Incomplete option groups');
    if (!groups.length && variants.length !== 1) throw new Error('Missing option controls');
    title.textContent = fragment.dataset.productTitle;
    document.title = `${fragment.dataset.productTitle} – ${originalTitle}`;
    // Every native option requires an explicit choice, including one-value groups.
    groups.forEach((group, index) => {
      const inputs = [...group.querySelectorAll('[data-quick-option-value]')];
      if (!inputs.length || variants.some(variant => !inputs.some(input => input.value === variant.options[index]))) throw new Error('Missing option value');
      inputs.forEach(input => { input.checked = inputs.length === 1; });
      const details = group.closest('[data-quick-option-group]');
      if (details) details.open = index === 0;
    });
    emit('nirala:quick-order-mounted');
    update();
    form.addEventListener('change', event => {
      if (busy) return;
      if (event.target.matches('[data-quick-option-value]')) {
        const changedIndex = groups.indexOf(event.target.closest('[data-quick-option-index]'));
        groups.forEach((group, index) => {
          if (/crust|base/i.test(groups[changedIndex]?.dataset.optionName || '') && /flavou?r/i.test(group.dataset.optionName || '')) group.querySelectorAll('[data-quick-option-value]').forEach(input => { input.checked = false; });
          const details = group.closest('[data-quick-option-group]');
          if (details) details.open = false;
        });
        syncConditionalOptions();
        const next = groups.find(group => !group.closest('[data-quick-option-group]').hidden && !group.querySelector('[data-quick-option-value]:checked'));
        const nextDetails = next?.closest('[data-quick-option-group]');
        if (nextDetails) nextDetails.open = true;
        (nextDetails || groups[changedIndex]?.closest('[data-quick-option-group]'))?.querySelector('summary')?.focus({ preventScroll: true });
        const variant = selectedVariant();
        if (variant) {
          const min = Math.max(1, Number(variant.min) || 1);
          const step = Math.max(1, Number(variant.step) || 1);
          const quantity = Number(state.quantity.value);
          if (!Number.isSafeInteger(quantity) || quantity < min || (quantity - min) % step || (variant.max !== null && variant.max !== undefined && quantity > Number(variant.max))) state.quantity.value = String(min);
        }
      }
      update();
    });
    state.quantity.addEventListener('input', () => { if (!busy) update(); });
    form.addEventListener('click', event => {
      const button = event.target.closest('[data-quick-quantity-change]');
      if (!button || busy) return;
      const min = Number(state.quantity.min) || 1;
      const step = Number(state.quantity.step) || 1;
      const current = Number(state.quantity.value);
      const direction = Number(button.dataset.quickQuantityChange);
      const offset = ((Number.isFinite(current) ? current : min) - min) / step;
      const next = min + (direction > 0 ? Math.floor(offset) + 1 : Math.ceil(offset) - 1) * step;
      const maximum = state.quantity.max ? min + Math.floor((Number(state.quantity.max) - min) / step) * step : Infinity;
      state.quantity.value = String(Math.max(min, Math.min(maximum, next)));
      update();
    });
    form.addEventListener('submit', submit);
    loading.hidden = true;
    dialog.removeAttribute('aria-busy');
    title.focus({ preventScroll: true });
  }
  function productPath(value) {
    const url = new URL(value, window.location.href);
    const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
    return match ? `/products/${match[1]}` : null;
  }
  function payload(fragment, expectedURL) {
    if (!fragment || productPath(fragment.dataset.productUrl || '') !== productPath(expectedURL)) throw new Error('The requested product options were not returned');
    const variants = validateVariants(JSON.parse(fragment.querySelector('[data-quick-order-variants]')?.textContent || 'null'), Number(fragment.dataset.variantCount));
    return { fragment, variants };
  }
  function embeddedPayload(url) {
    const expected = productPath(url);
    for (const template of document.querySelectorAll('template[data-quick-order-template]')) {
      if (productPath(template.dataset.productUrl || '') !== expected) continue;
      try { return payload(template.content.querySelector('[data-quick-order-product]')?.cloneNode(true), url); }
      catch (_) { /* An incomplete editor fragment falls back to a fresh Shopify section. */ }
    }
    return null;
  }
  function sectionURL(url) {
    // Collection-qualified links and tracking parameters must not change which product renders.
    const root = window.Shopify?.routes?.root || '/';
    const path = productPath(url);
    if (!path) throw new Error('Invalid product address');
    const canonical = new URL(`${root.replace(/\/$/, '')}${path}`, window.location.origin);
    canonical.searchParams.set('section_id', 'quick-order-product');
    const preview = url.searchParams.get('preview_theme_id') || new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) canonical.searchParams.set('preview_theme_id', preview);
    return canonical;
  }
  function fetchPayload(url, signal) {
    const controller = new AbortController();
    return new Promise((resolve, reject) => {
      let finished = false;
      let timer;
      const finish = (callback, value) => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        signal.removeEventListener('abort', cancel);
        callback(value);
      };
      const cancel = () => { controller.abort(); finish(reject, new DOMException('Options loading cancelled', 'AbortError')); };
      if (signal.aborted) { cancel(); return; }
      signal.addEventListener('abort', cancel, { once: true });
      timer = setTimeout(() => { controller.abort(); finish(reject, new Error('Options loading timed out')); }, 8000);
      Promise.resolve().then(() => fetch(sectionURL(url).href, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } }))
        .then(response => {
          if (!response?.ok) throw new Error('Product options could not be loaded');
          return response.text();
        })
        .then(html => {
          if (typeof html !== 'string' || !html.trim() || html.trim() === 'null') throw new Error('Product options were empty');
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          if (parsed.querySelector('[data-quick-order-unavailable]')) {
            const unavailable = new Error('This product needs its full page');
            unavailable.nativeOnly = true;
            throw unavailable;
          }
          return payload(parsed.querySelector('[data-quick-order-product]'), url);
        })
        .then(value => finish(resolve, value), error => finish(reject, error));
    });
  }
  async function open(link, fromHistory = false) {
    if (busy) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !productPath(url)) return;
    if (!fromHistory && !historyEntry) {
      history.pushState({ ...history.state, niralaQuickOrder: true }, '', url.pathname + url.search);
      historyEntry = true;
    } else if (fromHistory) historyEntry = true;
    else if (historyEntry) history.replaceState({ ...history.state, niralaQuickOrder: true }, '', url.pathname + url.search);
    request?.abort();
    const token = ++sequence;
    const controller = new AbortController();
    request = controller;
    trigger = link;
    state = null;
    body.replaceChildren();
    title.textContent = defaultTitle;
    failure.hidden = true;
    loading.hidden = false;
    fallback.href = url.href;
    dialog.setAttribute('aria-busy', 'true');
    closeButton.disabled = false;
    if (retryButton) retryButton.hidden = false;
    if (!dialog.open) dialog.showModal();
    const current = () => !controller.signal.aborted && token === sequence && dialog.open;
    const key = sectionURL(url).href;
    try {
      // Menu products already carry real Liquid option markup. Opening them is instant
      // and does not depend on a second section-rendering request succeeding.
      let result = embeddedPayload(url);
      const cached = fragmentCache.get(key);
      if (!result && cached && Date.now() - cached.time < 60000) result = payload(cached.fragment.cloneNode(true), url);
      if (!result) {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try { result = await fetchPayload(url, controller.signal); break; }
          catch (error) {
            if (!current() || error.name === 'AbortError' || error.nativeOnly || attempt === 1) throw error;
          }
        }
        if (result) {
          if (fragmentCache.size >= 12) fragmentCache.delete(fragmentCache.keys().next().value);
          fragmentCache.set(key, { time: Date.now(), fragment: result.fragment.cloneNode(true) });
        }
      }
      if (!current()) return;
      mount(document.importNode(result.fragment, true), result.variants);
    } catch (error) {
      if (!current() || error.name === 'AbortError') return;
      state = null;
      body.replaceChildren();
      loading.hidden = true;
      failure.hidden = false;
      loadError.textContent = dialog.dataset.failedMessage;
      if (retryButton) retryButton.hidden = Boolean(error.nativeOnly);
      dialog.removeAttribute('aria-busy');
      (retryButton && !retryButton.hidden ? retryButton : fallback).focus({ preventScroll: true });
    } finally {
      if (request === controller) request = null;
    }
  }
  async function submit(event) {
    if (!state) return;
    if (busy) { event.preventDefault(); return; }
    update();
    if (window.NiralaIncludedOptions && !window.NiralaIncludedOptions.validate(state.form)) { event.preventDefault(); return; }
    if (!state.variant?.available || state.add.disabled || !state.form.reportValidity()) {
      event.preventDefault();
      showError(!requiredOptionsComplete() ? dialog.dataset.requiredMessage : state.variant?.available ? dialog.dataset.quantityMessage : dialog.dataset.unavailableMessage);
      const firstRequired = state.groups.find(group => !group.querySelector('[data-quick-option-value]:checked'))?.closest('[data-quick-option-group]');
      if (firstRequired) { firstRequired.open = true; firstRequired.querySelector('summary')?.focus({ preventScroll: true }); }
      return;
    }
    // Ordinary product forms are still usable if the main theme helper is unavailable.
    if (typeof window.Nirala?.addToCart !== 'function') return;
    event.preventDefault();
    const active = state;
    setBusy(true);
    try {
      if (window.NiralaLocation && !window.NiralaLocation.isReady()) {
        closeLayer();
        const ready = await window.NiralaLocation.ensure();
        if (state !== active) return;
        if (!ready) {
          if (!dialog.open) dialog.showModal();
          return;
        }
        if (!dialog.open) dialog.showModal();
      }
      const result = await window.Nirala.addToCart(active.form, { openCart: false, stayOnPage: true });
      if (state !== active) return;
      if (result === false || result?.cancelled) { if (!dialog.open) dialog.showModal(); return; }
      if (result?.error) throw new Error(result.message || String(result.error));
      const detail = { variantId: active.variant.id, quantity: Number(active.quantity.value), result };
      closeLayer();
      clear();
      if (!result?.cartRefreshFailed) window.Nirala.tell?.(dialog.dataset.addedMessage);
      document.dispatchEvent(new CustomEvent('nirala:quick-order-added', { detail }));
    } catch (error) {
      if (state === active) {
        if (!dialog.open) dialog.showModal();
        showError(error.message || dialog.dataset.failedMessage);
      }
    } finally {
      setBusy(false);
      if (state === active && dialog.open) title.focus({ preventScroll: true });
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-quick-order-open]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    open(link);
  });
  document.addEventListener('nirala:addons-changed', event => {
    if (!busy && state && event.detail?.form === state.form) update();
  });
  retryButton?.addEventListener('click', () => { if (trigger) open(trigger); });
  closeButton.addEventListener('click', dismiss);
  dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
  dialog.addEventListener('close', () => {
    if (ignoredCloseEvents > 0) { ignoredCloseEvents -= 1; return; }
    if (!busy) clear();
  });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dismiss();
  });
  window.NiralaQuickOrder = { open, close: dismiss };
})();
